import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import gsap from 'gsap';
import { useGameStore } from '../../core/store';
import { play, playTypeKey, stopPopupTip } from '../audio';
import { LockerObjectiveTip } from './LockerObjectiveTip';
import './locker-mobile.css';

function playFieldKey(e: KeyboardEvent<HTMLInputElement>) {
  if (e.key === 'Backspace' || e.key === 'Delete') {
    playTypeKey({ backspace: true });
    return;
  }
  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    playTypeKey();
  }
}

/**
 * Mobile identity — sci-fi tip at top, slim editor at bottom,
 * jersey framed clear in the middle with a gap above the sheet.
 */
export function LockerIdentityMobile() {
  const sheet = useRef<HTMLElement>(null);
  const beat = useGameStore((s) => s.lockerBeat);
  const profile = useGameStore((s) => s.profile);
  const setName = useGameStore((s) => s.setName);
  const setNumber = useGameStore((s) => s.setNumber);
  const confirmLockerIdentity = useGameStore((s) => s.confirmLockerIdentity);
  const [printFlash, setPrintFlash] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    if (beat !== 'identity' || !sheet.current) return;
    setShowTip(false);
    stopPopupTip();

    const tl = gsap.timeline();
    tl.fromTo(
      sheet.current,
      { y: '110%', opacity: 0 },
      { y: '0%', opacity: 1, duration: 0.6, ease: 'power3.out' },
    );
    tl.add(() => setShowTip(true), 0);

    return () => {
      tl.kill();
      stopPopupTip();
    };
  }, [beat]);

  useEffect(() => {
    if (beat !== 'identity') return;
    setPrintFlash(true);
    const id = window.setTimeout(() => setPrintFlash(false), 280);
    return () => window.clearTimeout(id);
  }, [profile.name, profile.number, beat]);

  if (beat !== 'identity') return null;

  const dismissTip = () => {
    if (!showTip) return;
    setShowTip(false);
    stopPopupTip();
  };

  const onNameChange = (value: string) => {
    setName(value);
    if (value.length > 0 && showTip) dismissTip();
  };

  const onConfirm = () => {
    void play('silence', { gain: 0.5 });
    stopPopupTip();
    setShowTip(false);
    if (sheet.current) {
      gsap.to(sheet.current, {
        y: '110%',
        opacity: 0,
        duration: 0.4,
        ease: 'power2.in',
        onComplete: () => confirmLockerIdentity(),
      });
    } else {
      confirmLockerIdentity();
    }
  };

  return (
    <div className="locker-m">
      <LockerObjectiveTip
        className="hud-tip--mobile"
        visible={showTip}
        line="Choose your name and number."
      />

      <aside
        className={`locker-m__sheet${printFlash ? ' is-printing' : ''}`}
        ref={sheet}
        aria-label="Define your jersey"
        onPointerDown={dismissTip}
      >
        <div className="locker-m__handle" aria-hidden />
        <header className="locker-m__head">
          <p className="locker-m__eyebrow">Identity</p>
          <h2 className="locker-m__title">Define your jersey.</h2>
        </header>

        <form
          className="locker-m__form"
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm();
          }}
        >
          <div className="locker-m__row">
            <label className="locker-m__field locker-m__field--name">
              <span>Name</span>
              <input
                value={profile.name}
                maxLength={12}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
                enterKeyHint="next"
                onKeyDown={playFieldKey}
                onChange={(e) => onNameChange(e.target.value)}
                onInput={(e) =>
                  onNameChange((e.target as HTMLInputElement).value)
                }
                placeholder="PLAYER"
              />
            </label>
            <label className="locker-m__field locker-m__field--num">
              <span>No.</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={99}
                value={profile.number}
                onKeyDown={playFieldKey}
                onChange={(e) => setNumber(Number(e.target.value))}
              />
            </label>
          </div>

          <button type="button" className="locker-m__cta" onClick={onConfirm}>
            Step into the tunnel
          </button>
        </form>
      </aside>
    </div>
  );
}
